import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatINR } from "@/lib/format";
import { Trash2, Save, FolderOpen, Plus } from "lucide-react";
import { MONTH_LABELS } from "@/lib/calc";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SavedTrackers = ({
  monthly,
  historicalRate,
  actuals,
  setMonthly,
  setHistoricalRate,
  setActuals,
}) => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [currentId, setCurrentId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/trackers`);
      setItems(r.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const buildPayload = () => ({
    name: name.trim() || `Scenario ${new Date().toLocaleString()}`,
    monthly_compensation: monthly,
    historical_rate: historicalRate,
    months: MONTH_LABELS.map((m, i) => ({
      month: m,
      actual_revenue: Number(actuals[i]) || 0,
    })),
  });

  const handleSave = async () => {
    if (!monthly) {
      toast.error("Enter compensation before saving.");
      return;
    }
    setLoading(true);
    try {
      if (currentId) {
        await axios.put(`${API}/trackers/${currentId}`, buildPayload());
        toast.success("Scenario updated");
      } else {
        const r = await axios.post(`${API}/trackers`, buildPayload());
        setCurrentId(r.data.id);
        toast.success("Scenario saved");
      }
      await load();
    } catch (e) {
      toast.error("Save failed");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = async (id) => {
    try {
      const r = await axios.get(`${API}/trackers/${id}`);
      const t = r.data;
      setName(t.name);
      setMonthly(t.monthly_compensation);
      setHistoricalRate(t.historical_rate);
      const next = MONTH_LABELS.map((m) => {
        const found = (t.months || []).find((x) => x.month === m);
        return found ? found.actual_revenue : 0;
      });
      setActuals(next);
      setCurrentId(t.id);
      toast.success(`Loaded: ${t.name}`);
    } catch (e) {
      toast.error("Load failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/trackers/${id}`);
      if (id === currentId) setCurrentId(null);
      await load();
      toast.success("Scenario deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleNew = () => {
    setCurrentId(null);
    setName("");
    setActuals(new Array(12).fill(0));
    toast.info("New scenario started");
  };

  return (
    <aside
      className="border border-slate-200 bg-white p-6 no-print"
      data-testid="saved-trackers-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
            Scenarios
          </p>
          <h3 className="font-display text-xl mt-1 text-slate-900">
            Save &amp; Compare
          </h3>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleNew}
          data-testid="btn-new-scenario"
          className="text-slate-600 hover:text-slate-900"
        >
          <Plus className="w-4 h-4 mr-1" />
          New
        </Button>
      </div>

      <div className="space-y-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Scenario name (e.g. Mr. A - FY26)"
          className="h-10 border-slate-300"
          data-testid="input-scenario-name"
        />
        <Button
          className="w-full bg-blue-800 hover:bg-blue-900 text-white transition-colors duration-200"
          onClick={handleSave}
          disabled={loading}
          data-testid="btn-save-scenario"
        >
          <Save className="w-4 h-4 mr-2" />
          {currentId ? "Update Scenario" : "Save Scenario"}
        </Button>
      </div>

      <div className="mt-6">
        <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          Saved ({items.length})
        </p>
        <ul className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
          {items.length === 0 && (
            <li className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200">
              No saved scenarios yet.
            </li>
          )}
          {items.map((it) => (
            <li
              key={it.id}
              className={`border p-3 group hover:border-blue-300 transition-colors duration-200 ${
                it.id === currentId ? "border-blue-500 bg-blue-50/40" : "border-slate-200"
              }`}
              data-testid={`saved-item-${it.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {it.name}
                  </p>
                  <p className="text-xs text-slate-500 font-mono-num">
                    {formatINR(it.monthly_compensation)}/mo ·{" "}
                    {(it.historical_rate * 100).toFixed(0)}%
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleLoad(it.id)}
                    className="p-1.5 text-slate-500 hover:text-blue-700 hover:bg-blue-50 transition-colors duration-150"
                    title="Load"
                    data-testid={`btn-load-${it.id}`}
                  >
                    <FolderOpen className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(it.id)}
                    className="p-1.5 text-slate-500 hover:text-rose-700 hover:bg-rose-50 transition-colors duration-150"
                    title="Delete"
                    data-testid={`btn-delete-${it.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
};

export default SavedTrackers;
